import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';

function PublicProductShow() {
    const { slug } = useParams();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                // Determine if we should use public or internal API? 
                // We use the new public endpoint we created
                const response = await api.get(`/public/products/${slug}`);

                if (response.error) {
                    setError(response.error);
                } else if (response.redirect_to) {
                    // Internal redirect advice
                    window.location.href = response.redirect_to;
                } else if (response.access === 'public') {
                    setProduct(response.product);
                } else {
                    setError('Unknown response type');
                }
            } catch (err) {
                console.error("Public product fetch error", err);
                setError("Product not found or unavailable.");
            } finally {
                setLoading(false);
            }
        };

        fetchProduct();
    }, [slug]);

    if (loading) return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
            <div className="animate-pulse flex flex-col items-center">
                <div className="h-12 w-12 bg-indigo-200 rounded-full mb-4"></div>
                <div className="h-4 w-48 bg-slate-200 rounded"></div>
            </div>
        </div>
    );

    if (error) return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
            <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-6">
                    <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                </div>
                <h1 className="text-2xl font-bold text-slate-800 mb-2">Product Unavailable</h1>
                <p className="text-slate-500 mb-8">{error}</p>
                <Link to="/" className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition-colors">
                    Go to Home
                </Link>
            </div>
        </div>
    );

    if (!product) return null;

    return (
        <div className="min-h-screen bg-slate-50 font-sans">
            {/* Simple Public Header */}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center text-white font-bold">
                            {product.firm.name.charAt(0)}
                        </div>
                        <span className="font-bold text-slate-800 text-lg">{product.firm.name}</span>
                    </div>
                    <Link to={`/store/firm/${product.firm.slug}`} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
                        Visit Store
                    </Link>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden grid grid-cols-1 md:grid-cols-2">
                    {/* Image Section */}
                    <div className="p-8 bg-slate-50 flex items-center justify-center border-b md:border-b-0 md:border-r border-slate-200">
                        {product.images && product.images.length > 0 ? (
                            <img src={product.images[0]} alt={product.name} className="max-h-96 w-auto object-contain mix-blend-multiply" />
                        ) : (
                            <div className="w-64 h-64 bg-slate-200 rounded-2xl flex items-center justify-center text-slate-400">
                                <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                        )}
                    </div>

                    {/* Content Section */}
                    <div className="p-8 md:p-12 flex flex-col">
                        <h1 className="text-3xl font-bold text-slate-900 mb-4">{product.name}</h1>

                        <div className="flex items-baseline gap-4 mb-8">
                            {product.selling_price ? (
                                <span className="text-4xl font-bold text-slate-900">₹{product.selling_price.toLocaleString()}</span>
                            ) : (
                                <span className="text-2xl font-bold text-slate-400">Price on Request</span>
                            )}

                            {product.stock_status && (
                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${product.stock_status === 'In Stock' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                                    {product.stock_status}
                                </span>
                            )}
                        </div>

                        <div className="prose prose-slate mb-8 max-w-none">
                            <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-2">Description</h3>
                            <p className="text-slate-600 whitespace-pre-wrap">{product.description || "No description available."}</p>
                        </div>

                        <div className="mt-auto pt-8 border-t border-slate-100">
                            <button className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-lg shadow-lg shadow-indigo-200 transition-all active:scale-[0.98]">
                                Contact Store / Buy Now
                            </button>
                            <p className="text-center text-xs text-slate-400 mt-4">
                                Secure transaction processed by Inventory App
                            </p>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}

export default PublicProductShow;
